/* lib/api/keyedAttempt.js — ПОПЫТКА создания с ключом идемпотентности у редакторов «Продукции» и
   формы цены (контракт idempotency-key, шрам S30). lib/api/idempotency.js — ключ, заголовок и разбор
   ответов сервера; здесь — что значит отказ для попытки, которая живёт дольше одного запроса.

   · runKeyed(run, opts) — retryOnceIfUnknown (один автоматический повтор тем же запросом при
     неизвестном исходе), и отказ ПОВТОРА помечается `priorUnknown`: исход первого запроса так и
     неизвестен, а отказ повтора (429, 403, замок сессии…) ключ не решает — первая попытка могла лечь;
   · keyedFailure(e, { unknownBefore }) → { kind, unknown } — что значит отказ:
       'reused'  — 422 idempotency_key_reused: запись первой попытки есть (`existing`), набранное
                   после сбоя не записано; ключ решён;
       'gone'    — 410 idempotency_object_gone: запись первой попытки удалена; ключ решён —
                   следующей попытке новый;
       'unknown' — исход неизвестен (нет ответа, 408, любой 5xx — и после автоматического повтора)
                   ИЛИ отказ, который исход не решает (settlesKeyedAttempt), после неизвестного исхода
                   этой попытки (её прошлого запроса — unknownBefore — или первого запроса этого
                   вызова — priorUnknown): «неизвестно… дубля не будет», ключ тот же;
       'refused' — известный отказ: неизвестности не было или отказ исход решает.
     unknown — новое «исход попытки неизвестен» (липкое, пока ключ не решён);
   · settlesKeyedAttempt(e, { manual }) — решает ли отказ исход попытки (то же правило — у таблицы,
     RegistryTable: один ответ на «решён ли ключ» во всей «Продукции», S30 п. 6в);
   · keyedUnknownText({ what, again }, e) — честный текст неизвестного исхода;
   · mayStillBeWriting(e), stillWritingText({ created, again }) — шаг БЕЗ ключа после создания (состав
     ТТК, разделы меню) кончился 504 или без ответа: сервер может ещё дописывать — «дубля не будет»
     не обещаем;
   · goneText(e) — текст сервера на 410 (нет — слова контракта);
   · attemptKeyOf(v) — ключ из префилла («Открыть полностью»), если он годится заголовку.

   Чистый модуль: без React и без window. */

import { isKeyedCreateUnknown, isKeyGone, isKeyReused, KEY_GONE_TEXT, retryOnceIfUnknown } from './idempotency.js';

/** Попытка с ключом: run(attempt, previousError) — как у retryOnceIfUnknown (бросает отказ, unwrap
 *  внутри). Отказ автоматического повтора — с пометкой priorUnknown. */
export async function runKeyed(run, opts) {
  let retried = false;
  try {
    return await retryOnceIfUnknown((attempt, prev) => {
      if (attempt > 0) retried = true;
      return run(attempt, prev);
    }, opts);
  } catch (e) {
    if (retried && e && typeof e === 'object' && !isKeyedCreateUnknown(e)) {
      try { e.priorUnknown = true; } catch { /* замороженный объект — пометку не ставим */ }
    }
    throw e;
  }
}

const codeOf = (e) => {
  if (!e || typeof e !== 'object') return null;
  if (typeof e.code === 'string') return e.code;
  return e.data && typeof e.data === 'object' && typeof e.data.code === 'string' ? e.data.code : null;
};
const statusOf = (e) => (e && typeof e === 'object' && typeof e.status === 'number' ? e.status : null);

/* Отказы, которые сервер даёт ПОСЛЕ поиска ключа (contract.order_of_checks, шаг 6): ключ ничего не
   держит — запись первой попытки не легла (дубль сервер перед отказом ищет по ключу ещё раз). По коду:
   у 400 сериализатора кода нет. Пустое название меню отвечает тоже после ключа, но без кода — его не
   отличить от 400 сериализатора, и оно остаётся «неизвестно» (безопасная сторона; форма меню без
   названия «Создать» не шлёт). */
const POST_LOOKUP_CODES = new Set(['duplicate_title', 'duplicate_name', 'name_required', 'card_not_found']);

/** Решает ли отказ исход попытки с ключом (S30 п. 6в; то же правило — у таблицы, RegistryTable):
 *  · 410 и 422 — да (запись первой попытки удалена / легла с другим телом);
 *  · отказ ПОСЛЕ поиска ключа (POST_LOOKUP_CODES) — да;
 *  · 400 сериализатора и 404 без кода сервер отвечает ДО поиска ключа: они решают исход, только когда
 *    отказан АВТОМАТИЧЕСКИЙ повтор (manual: false) — он несёт то же тело, что и потерянный запрос, и
 *    сериализатор отказал бы обоим. Ручной повтор (manual: true: прошлый вызов этой попытки кончился
 *    неизвестно чем) мог уйти с поправленными полями — его 400 об исходе прошлой попытки не говорит
 *    ничего;
 *  · отказ до записи (401, 403, 409 замка сессии, 429…) и неизвестный исход — нет. */
export function settlesKeyedAttempt(e, { manual = false } = {}) {
  const s = statusOf(e);
  if (s === 410 || s === 422) return true;
  if (POST_LOOKUP_CODES.has(codeOf(e))) return true;
  return !manual && (s === 400 || s === 404);
}

export function keyedFailure(e, { unknownBefore = false } = {}) {
  if (isKeyReused(e)) return { kind: 'reused', unknown: false };
  if (isKeyGone(e)) return { kind: 'gone', unknown: false };
  if (isKeyedCreateUnknown(e)) return { kind: 'unknown', unknown: true };
  const manual = !!unknownBefore;
  const prior = manual || !!(e && e.priorUnknown);
  if (!prior || settlesKeyedAttempt(e, { manual })) return { kind: 'refused', unknown: false };
  return { kind: 'unknown', unknown: true };
}

const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
// причина отказа повтора словами — без точки в конце (фраза продолжается)
function refusalWhy(e) {
  if (e && e.status === 429) return 'слишком много запросов, подождите минуту';
  if (codeOf(e) === 'session_changed') return 'в этом браузере сменилась сессия';
  const detail = e && typeof e.detail === 'string' ? e.detail.trim().replace(/[.\s]+$/, '') : '';
  return detail ? lowerFirst(detail) : 'сервер не принял запрос';
}

/** what — «записалась ли цена» / «создалась ли ТТК»; again — «„Повторить“ безопасен» /
 *  «Нажмите «Создать ТТК» ещё раз». Ни «не записано», ни «не сохранено»: запись могла лечь. */
export function keyedUnknownText({ what, again }, e) {
  const tail = ' ' + again + ': дубля не будет.';
  if (!isKeyedCreateUnknown(e)) return 'Неизвестно, ' + what + ': сервер не ответил, а повтор отклонён — ' + refusalWhy(e) + '.' + tail;
  if (e && e.status === 504) return 'Ответ не пришёл вовремя — неизвестно, ' + what + ': сервер может ещё дописывать.' + tail;
  return 'Сервер не ответил — неизвестно, ' + what + '.' + tail;
}

/** Шаг БЕЗ ключа после создания (состав ТТК rows/bulk, разделы меню) кончился 504 или без ответа
 *  (сеть, AbortError, статус 0): воркер мог ещё дописывать — повтор, спланированный сейчас против
 *  сервера, задвоил бы строки или разделы, которые допишутся позже (S30). Офлайн до отправки (notSent)
 *  — не ушло; 502/503/500 — воркер уже не пишет. → true — «дубля не будет» не обещать. */
export function mayStillBeWriting(e) {
  if (!e || typeof e !== 'object' || e.notSent) return false;
  if (e instanceof TypeError || e.name === 'AbortError') return true;
  const s = statusOf(e);
  return s === 0 || s === 504;
}

/** created — «ТТК создана» / «Меню создано»; again — «Нажмите «Создать ТТК»». Слова 504 —
 *  как у keyedUnknownText: сервер может ещё дописывать; повтор — после паузы, без обещания. */
export function stillWritingText({ created, again }) {
  return created + ', но ответ на сохранение не пришёл — сервер может ещё дописывать. Подождите минуту и '
    + lowerFirst(again) + ' ещё раз.';
}

/** 410 idempotency_object_gone: текст сервера (на ошибке unwrap или в теле конверта), иначе — контракта. */
export function goneText(e) {
  const src = e && typeof e === 'object' ? (typeof e.detail === 'string' ? e : e.data) : null;
  const detail = src && typeof src === 'object' && typeof src.detail === 'string' ? src.detail.trim() : '';
  return detail || KEY_GONE_TEXT;
}

const KEY_SHAPE = /^[A-Za-z0-9._:-]{8,128}$/;
/** Ключ попытки из префилла: строка в форме заголовка — она; иначе null (запрос ушёл бы с 400). */
export function attemptKeyOf(v) {
  return typeof v === 'string' && KEY_SHAPE.test(v) ? v : null;
}

/** Предупреждение прошлой попытки редактора (тост «неизвестно…», «создана, но…», «удалена»)
 *  устаревает, как только человек начинает следующую попытку того же создания: снять его, чтобы
 *  рядом с тостом успеха не висело «неизвестно» (живой QA O2). id тоста — в `attempt.toastId`
 *  (useToast().show → id); `toast.dismiss` нет (заглушка в тесте) — только забыть id. */
export function dismissAttemptToast(toast, attempt) {
  if (!attempt || attempt.toastId == null) return;
  if (toast && typeof toast.dismiss === 'function') {
    try { toast.dismiss(attempt.toastId); } catch { /* показ — не запись */ }
  }
  attempt.toastId = null;
}
